import { Config } from '../../helper';

// Single source of truth for view-mode fields. The settings-form inputs, the empty presets,
// and the required-field validation are all derived from this catalog — adding or renaming a
// field is a one-line change here. `mapping.*` names are stored under the mode's `mapping`
// object; top-level names (the card flags) sit alongside it. Field identifiers reference
// Config.Mapping so the form and the runtime reader (helper.js) cannot drift apart.
export const VIEW_MODE_FIELDS = {
  [Config.ViewModes.Card]: [
    {kind: 'checkbox', name: 'imagePadding', label: 'Image padding'},
    {kind: 'checkbox', name: 'clickableCard', label: 'Clickable card'},
    {kind: 'checkbox', name: 'truncatePretitle', label: 'Truncate pretitle'},
    {kind: 'text', name: `mapping.${Config.Mapping.Card.Featured}`, label: 'Featured'},
    {kind: 'text', name: `mapping.${Config.Mapping.Card.Badges}`, label: 'Badges'},
    {kind: 'text', name: `mapping.${Config.Mapping.Card.ImageURL}`, label: 'Image URL'},
    {kind: 'text', name: `mapping.${Config.Mapping.Card.Pretitle}`, label: 'Pretitle'},
    {kind: 'text', name: `mapping.${Config.Mapping.Card.Title}`, label: 'Title', required: true},
    {kind: 'text', name: `mapping.${Config.Mapping.Card.Subtitle}`, label: 'Subtitle'},
    {kind: 'text', name: `mapping.${Config.Mapping.Card.Body}`, label: 'Body'},
    {kind: 'text', name: `mapping.${Config.Mapping.Card.Links}`, label: 'Links'},
  ],
  [Config.ViewModes.Accordion]: [
    {kind: 'text', name: `mapping.${Config.Mapping.Accordion.ImageURL}`, label: 'Image URL'},
    {kind: 'text', name: `mapping.${Config.Mapping.Accordion.Title}`, label: 'Title', required: true},
    {kind: 'text', name: `mapping.${Config.Mapping.Accordion.Badge}`, label: 'Badge'},
    {kind: 'text', name: `mapping.${Config.Mapping.Accordion.Body}`, label: 'Body'},
    {kind: 'text', name: `mapping.${Config.Mapping.Accordion.Markdown}`, label: 'Markdown'},
    {
      kind: 'textarea', name: `mapping.${Config.Mapping.Accordion.DescriptionListLabels}`,
      label: 'Description list labels', rows: 7,
      hint: 'Optional newline-separated label overrides, one per line, matched by index to "Description list".',
      className: 'col-6',
    },
    {
      kind: 'textarea', name: `mapping.${Config.Mapping.Accordion.DescriptionList}`,
      label: 'Description list', rows: 7,
      hint: 'Newline-separated field paths to render as a label/value list under each result.',
      className: 'col-6',
    },
    {kind: 'text', name: `mapping.${Config.Mapping.Accordion.Links}`, label: 'Links'},
  ],
};

export const viewModeLabels = {
  [Config.ViewModes.Card]: 'Card',
  [Config.ViewModes.Accordion]: 'Accordion',
};

export const viewModeOptions = Object.values(Config.ViewModes).map(
  mode => [mode, viewModeLabels[mode] || mode]
);

const MAPPING_PREFIX = 'mapping.';

// Empty config for a mode, derived from its field catalog (flags → false, fields → '').
function buildPreset(fields) {
  const preset = {mapping: {}};
  for (const field of fields) {
    if (field.name.startsWith(MAPPING_PREFIX)) {
      preset.mapping[field.name.slice(MAPPING_PREFIX.length)] = '';
    } else {
      preset[field.name] = field.kind === 'checkbox' ? false : '';
    }
  }
  return preset;
}

export const viewModePresets = Object.fromEntries(
  Object.entries(VIEW_MODE_FIELDS).map(([mode, fields]) => [mode, buildPreset(fields)])
);

// Mapping keys that must be filled for a given mode (drives the form's validation schema).
export function requiredMappingKeys(mode) {
  return (VIEW_MODE_FIELDS[mode] || [])
    .filter(field => field.required && field.name.startsWith(MAPPING_PREFIX))
    .map(field => field.name.slice(MAPPING_PREFIX.length));
}

// Fresh per-mode config object (deep copy of presets) so every provider keeps an
// independent config for each view mode and switching never loses the other mode's data.
export function freshViewModeConfigs() {
  return Object.fromEntries(
    Object.entries(viewModePresets).map(([mode, preset]) => [
      mode,
      {...preset, mapping: {...preset.mapping}},
    ])
  );
}

export const initialValues = {
  title: '',
  displayOptions: {
    layout: 'standard',
    showFacetsCount: false,
    stickyNavigation: false,
    hideCategoryLabel: false,
    itemsPerPage: 12,
  },
  compactMode: {
    dropdownLabel: '',
    cta: {
      text: '',
      url: '',
    }
  },
  openSearchProviders: [],
};

export const openSearchProviderDetails = {
  provider: 'opensearch',
  id: '',
  indexes: '',
  filters: '',
  aggregation: '',
  sorting: [],
  facets: [],
  viewMode: Config.ViewModes.Card,
  viewModeConfigs: freshViewModeConfigs(),
};

export const openSearchSortingDefaults = {
  label: '',
  field: '',
  direction: '',
};

export const openSearchFacetDefaults = {
  id: '',
  field: '',
  label: '',
  hidden: false,
  // Empty = checkboxes (the default rendering); see facetWidgetOptions / Config.FacetWidgets.
  widget: '',
};

// Per-facet widget options (standard layout). Empty renders the default inline checkbox list;
// 'multiselect' renders a dropdown panel of checkboxes; 'dropdown' renders a native single-select.
export const facetWidgetOptions = [
  ['', 'Checkboxes'],
  [Config.FacetWidgets.Multiselect, 'Dropdown (multi-select)'],
  [Config.FacetWidgets.Dropdown, 'Dropdown (single-select)'],
];

// Legacy back-compat (form-load shim): published pages store the old per-mode arrays
// (cardViewModes / accordionViewModes) and are not auto-migrated. Normalize them to the
// nested { viewMode, viewModeConfigs: { <mode>: { mapping, ...flags } } } shape
// before rendering the form, otherwise opening an old page shows blank fields and
// silently wipes the live config on save. Remove once all live pages are re-saved
// into the new shape.
export function normalizeProvider(provider) {
  if (!provider) return provider;

  // Pull the legacy/flat config keys off the root; the rest passes through untouched. These
  // are folded into the nested `viewModeConfigs` shape below rather than dropped, so
  // opening an old page never wipes or flips its config. They never leak back to the
  // blob — the returned object only carries `viewMode` + `viewModeConfigs`.
  const {
    cardViewModes, accordionViewModes,
    mapping, imagePadding, clickableCard, truncatePretitle,
    viewMode, viewModeConfigs,
    ...rest
  } = provider;

  // Already the new nested shape — backfill any missing mode with its preset.
  if (viewModeConfigs) {
    return {
      ...rest,
      viewMode: viewModePresets[viewMode] ? viewMode : Config.ViewModes.Card,
      viewModeConfigs: {...freshViewModeConfigs(), ...viewModeConfigs},
    };
  }

  const nextViewModeConfigs = freshViewModeConfigs();
  const isKnownMode = (mode) => Object.prototype.hasOwnProperty.call(nextViewModeConfigs, mode);

  // Honor a serialized active mode when it's one we can render; otherwise let the data decide.
  let nextViewMode = isKnownMode(viewMode) ? viewMode : null;

  // Mappings are merged over the mode's preset so every catalog field stays present (with an
  // empty default) even when legacy data only filled a subset — avoids uncontrolled inputs.
  if (Array.isArray(cardViewModes) && cardViewModes[0]) {
    const {id, ...vm} = cardViewModes[0];
    nextViewModeConfigs[Config.ViewModes.Card] = {
      imagePadding: vm.imagePadding ?? false,
      clickableCard: vm.clickableCard ?? false,
      truncatePretitle: vm.truncatePretitle ?? false,
      mapping: {...nextViewModeConfigs[Config.ViewModes.Card].mapping, ...(vm.mapping || {})},
    };
    if (!nextViewMode) nextViewMode = Config.ViewModes.Card;
  }

  if (Array.isArray(accordionViewModes) && accordionViewModes[0]) {
    const {id, ...vm} = accordionViewModes[0];
    nextViewModeConfigs[Config.ViewModes.Accordion] = {
      ...nextViewModeConfigs[Config.ViewModes.Accordion],
      mapping: {...nextViewModeConfigs[Config.ViewModes.Accordion].mapping, ...(vm.mapping || {})},
    };
    if (!nextViewMode) nextViewMode = Config.ViewModes.Accordion;
  }

  // Transitional flat shape { viewMode, mapping, ...flags } from pre-nested builds: fold the
  // root-level config into its mode so the form doesn't wipe it on open/save.
  const hasFlatConfig = mapping !== undefined
    || imagePadding !== undefined
    || clickableCard !== undefined
    || truncatePretitle !== undefined;
  if (isKnownMode(viewMode) && hasFlatConfig) {
    nextViewModeConfigs[viewMode] = {
      ...nextViewModeConfigs[viewMode],
      ...(imagePadding !== undefined ? {imagePadding} : {}),
      ...(clickableCard !== undefined ? {clickableCard} : {}),
      ...(truncatePretitle !== undefined ? {truncatePretitle} : {}),
      mapping: {...nextViewModeConfigs[viewMode].mapping, ...(mapping || {})},
    };
  }

  return {...rest, viewMode: nextViewMode || Config.ViewModes.Card, viewModeConfigs: nextViewModeConfigs};
}

export function normalizeSettings(settings) {
  if (!settings || !Array.isArray(settings.openSearchProviders)) {
    return settings;
  }

  return {
    ...settings,
    openSearchProviders: settings.openSearchProviders.map(normalizeProvider),
  };
}
